// ---------------------------------------------------------------------------
// Source-import activity — the ONE piece of shared state for the .fig import
// journey (UX audit, 2026-07-27).
//
// Before this, a running import lived only in a component's local `useState`
// and a failed import lived only inside the blocked bundle's gate message. So
// the import page navigated away mid-failure, `/ide/source-workspace` had no
// idea an import was in flight, and the cockpit projection (`>>>` + badge,
// Studio UX canon §5) never learned that a blocker existed.
//
// This record is written by whichever surface starts the import and read by
// every surface that must tell the truth about it:
//   - studio-cockpit-selectors.ts  → cockpit/Status attention routing
//   - shell/ide-v2/surfaces/source-surface.tsx → non-blank canvas states
//   - features/studio-dashboard/pages/import-page.tsx → failure at the moment
//
// It is deliberately NOT a second notification system: the cockpit projection
// consumes it as one more input, exactly like validator errors do.
// ---------------------------------------------------------------------------

import type { OpenPencilImportFailureReason } from "./openpencil-import-failure";

export interface OpenPencilImportActivity {
  /** `importing` = work in progress; `blocked` = it failed and stayed failed. */
  phase: "importing" | "blocked";
  /** The picked file — shown to the creator so the state is never abstract. */
  fileName: string;
  /** Creator-chosen source name, when the surface had one. */
  sourceName?: string;
  /** Localized progress label for the in-flight phase. */
  statusLabel?: string;
  /** Localized failure title for the blocked phase (copy module output). */
  title?: string;
  /** Closed typed reason — the only thing UI may key failure copy off. */
  reason?: OpenPencilImportFailureReason;
  /**
   * Retained transport detail (raw upstream text). Evidence for developer
   * surfaces and logs ONLY — never rendered as creator copy, never used to
   * build a cockpit title.
   */
  detail?: string;
  startedAt: number;
  updatedAt: number;
}

/**
 * Cockpit-safe title. Built from the localized copy title (or a neutral
 * fallback) plus the file name — never from `detail`, so no HTTP status or
 * upstream body can reach the tab strip.
 */
export function openPencilImportActivityTitle(
  activity: OpenPencilImportActivity,
): string {
  const prefix =
    activity.phase === "blocked"
      ? (activity.title ?? "Source import is blocked")
      : (activity.statusLabel ?? "Reading source file");
  return `${prefix} · ${activity.fileName}`;
}
